import {
  API_ENDPOINT,
  API_ENDPOINT_V3,
  API_GATEWAY_ENDPOINT,
  API_GATEWAY_ENDPOINT_V3,
  CLOUD_FRONT_ABSOLUTE_ENDPOINT,
  CODEBUILD_BUILD_NUMBER,
  CODEBUILD_GIT_SHORT_ID,
  EARNINGS_CALL_DOT_DEV_ENDPOINT,
  ATLAS_FLEET_ENDPOINT,
} from "./api-config.mjs";

// https://github.com/JustinBeckwith/retry-axios
import * as rax from "retry-axios";
import axios from "axios";
import { addDays, subDays, startOfDay, endOfDay } from "date-fns";

// https://daveceddia.com/multiple-environments-with-react/

import log from "loglevel";
import { getSymbol } from "./components/Symbols.mjs";
import { getBehaviorSignalHeader } from "./utils/behaviorSignalGlobal.mjs";

rax.attach();

async function doGet(url = "") {
  const response = await fetch(url);
  if (!response.ok) {
    let error = {
      status: response.status,
      statusText: response.statusText,
    };
    error.responseBody = await response.text();
    log.warn("doGet()", error);
    throw error;
  }
  return await response.json();
}

export async function connectWebSocket(url, options = {}) {
  const {
    onOpen = () => {},
    onMessage = () => {},
    onError = () => {},
    onClose = () => {},
    protocols = []
  } = options;

  const ws = new WebSocket(url, protocols);

  ws.onmessage = (event) => {
    log.debug('WebSocket message received:', event.data);
    onMessage(event);
  };

  ws.onerror = (event) => {
    log.error('WebSocket error:', event);
    onError(event);
  };

  ws.onclose = (event) => {
    log.debug('WebSocket closed');
    onClose(event);
  };

  // Wait for connection to open
  await new Promise((resolve, reject) => {
    ws.onopen = (event) => {
      log.debug('WebSocket connected');
      onOpen(event);
      resolve();
    };
    ws.onerror = (event) => {
      reject(event);
    };
  });

  return ws;
}

export async function sendWebSocketMessage(ws, message) {
  if (ws.readyState === WebSocket.OPEN) {
    try {
      ws.send(typeof message === 'string' ? message : JSON.stringify(message));
    } catch (error) {
      log.error('Error sending WebSocket message:', error);
      throw error;
    }
  } else {
    throw new Error('WebSocket is not open');
  }
}

export async function fetchEarnings(
  last_evaluated_key = null,
  event_id = null,
  filter_condition = null
) {
  let queryParams = "";
  if (last_evaluated_key) {
    queryParams = `last_evaluated_key=${last_evaluated_key}`;
  }
  if (event_id) {
    queryParams = `event_id=${event_id}`;
  }
  if (filter_condition) {
    if (!!queryParams) {
      queryParams += `&${filter_condition}`;
    } else {
      queryParams = filter_condition;
    }
  }

  const url = `${API_ENDPOINT_V3}/earnings?${queryParams}`;
  log.debug("loading url", url);
  const response = await fetch(url);
  if (!response.ok) {
    log.warn("Response Not OK:", response);
    throw response;
  }
  return response.json();
}

function authHeaderPair(xAuthType, xAuthToken) {
  return {
    "x-auth-type": xAuthType,
    "x-auth-token": xAuthToken,
  };
}

export function extractAuthHeaders({ context }) {
  const { sessionContext } = context || {};
  const {
    session,
    google,
    facebook,
    test,
    appBuilderKey,
    loginWithEmail,
  } = sessionContext || {};

  if (session) {
    return authHeaderPair("session", session.session_id);
  } else if (google) {
    return authHeaderPair("google-web-code", google.code);
  } else if (facebook) {
    return authHeaderPair("facebook-web", facebook.accessToken);
  } else if (test) {
    return authHeaderPair("test", test.testAuthToken);
  } else if (appBuilderKey) {
    return authHeaderPair("app-builder", appBuilderKey);
  } else if (loginWithEmail) {
    return authHeaderPair("email", btoa(JSON.stringify(loginWithEmail)));
  }
  return {};
}

async function getRequestHeaders(params) {
  const headers = {
    ...extractAuthHeaders(params),
    "x-client-build-version": CODEBUILD_BUILD_NUMBER,
    "x-client-commit-id": CODEBUILD_GIT_SHORT_ID,
    "x-client-device-type": "web",
  };

  // Add behavior signal with nonce if available
  const behaviorSignal = await getBehaviorSignalHeader();
  if (behaviorSignal) {
    headers["x-behavior-signal"] = behaviorSignal;
  }

  return headers;
}

export async function doAxiosGet(params) {
  return await doAxiosRequest("get", params);
}

export async function doAxiosPost(params) {
  return await doAxiosRequest("post", params);
}

export async function doAxiosDelete(params) {
  return await doAxiosRequest("delete", params);
}

export async function doAxiosRequest(method, params) {
  const {
    url,
    queryParams = {},
    data,
    cancelToken,
    signal,
  } = params;
  // log.debug("loading url", url, queryParams, data, params);
  try {
    const headers = await getRequestHeaders(params);
    const { data: result } = await axios({
      method,
      url,
      params: queryParams,
      data,
      headers,
      cancelToken,
      signal,
      // timeout: 30000,
    });
    return result;
  } catch (error) {
    // https://github.com/axios/axios#handling-errors
    log.error(
      `doAxiosRequest() failed for ${method}:${url} ${JSON.stringify(
        queryParams
      )}`
    );
    if (error.response) {
      log.error(`Server responded with HTTP status: ${error.response.status}`);
      // A failed request (e.g. a 404 that returns the entire SPA HTML) can have
      // a huge response body; logging it whole floods the logs. Truncate it.
      const MAX_BODY_LOG_CHARS = 1000;
      let body = error.response.data;
      if (typeof body !== "string") {
        try {
          body = JSON.stringify(body);
        } catch {
          body = String(body);
        }
      }
      log.error(
        body && body.length > MAX_BODY_LOG_CHARS
          ? `Server response body (${body.length} chars, truncated): ${body.slice(0, MAX_BODY_LOG_CHARS)}…`
          : `Server response body: ${body}`
      );
      log.error(error.response.headers);
    } else if (error.request) {
      // The request was made but no response was received
      // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
      // http.ClientRequest in node.js
      log.error("The request was made but no response was received.");
      log.error(error.request);
    } else {
      log.error("Something happened in setting up the request that triggered an Error");
      log.error("Error", error.message);
    }
    throw error;
  }
}

export async function getPageData(params) {
  const { pageDataPath } = params;
  const url = `/page-data/${pageDataPath}/page-data.json`;
  return doAxiosGet({ url, ...params });
}

export async function getECSBuildTasks(params) {
  return doAxiosGet({ url: `${API_ENDPOINT}/build/tasks`, ...params });
}

export async function getMobileAppBuilds(params) {
  return doAxiosGet({ url: `${API_ENDPOINT}/mobile/builds`, ...params });
}

export async function getNews(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/news`, ...params });
}

export async function userAuthSetup(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT}/user/auth/setup`,
    ...params,
  });
}

export async function newLeadForm(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT}/intake/lead`,
    ...params,
  });
}

// Quiz funnel (see cca documentation/32-quiz-funnel-and-self-improvement.md). One endpoint:
// GET /a2/quiz starts a session, POST /a2/quiz dispatches on `operation`. The operation is
// injected here rather than by callers, so a page just calls submitQuizAnswer/completeQuiz.
//
// The backend issues the session id and decides which phrasing of each question this visitor
// sees, so the client must not cache or reuse a session across visits -- that would put one
// respondent into both arms of a running split test.
export async function startQuiz(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quiz`, ...params });
}

export async function submitQuizAnswer(params) {
  const { data, ...rest } = params;
  return await doAxiosPost({
    url: `${API_ENDPOINT}/quiz`,
    data: { ...data, operation: "answerQuestion" },
    ...rest,
  });
}

export async function completeQuiz(params) {
  const { data, ...rest } = params;
  return await doAxiosPost({
    url: `${API_ENDPOINT}/quiz`,
    data: { ...data, operation: "completeQuiz" },
    ...rest,
  });
}

export async function postNews(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/news`, ...params });
}

export async function getEarnings(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT_V3}/earnings`, ...params });
}

export async function getQuartrCalendar(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quartr/calendar`, ...params });
}

export async function getQuartrEventDetails(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quartr/event/details`, ...params });
}

export async function getQuartrCompanySearch(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quartr/search/company`, ...params });
}

export async function newEarnings(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/earnings`, ...params });
}

export async function deleteEarnings(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT_V3}/earnings`, ...params });
}

export async function callUseSlideDeckUrl(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/earnings/use/slides`, ...params });
}

export async function findSlideDeck(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/earnings/find-slide-deck`, ...params });
}

export async function getNotifications(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/notifications`, ...params });
}

export async function saveNotification(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications`, ...params });
}

export async function getBlog(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/blog`, ...params });
}

export async function saveBlog(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/blog`, ...params });
}

export async function deleteBlog(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/blog`, ...params });
}

export async function recordEarnings(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT_V3}/earnings/record`,
    ...params,
  });
}

// Alpha-only. Launches a long-form (~50 min) LIVEDEMO test recording so live
// features (livestream, live viewer, live audio) can be exercised without waiting
// on a real call. Backs the "Start test recording" button on the admin /test page.
// The endpoint is registered at /a2/test/recording only (no /a3 clone), so this
// must use API_ENDPOINT (not API_ENDPOINT_V3).
export async function startTestRecording(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/test/recording`, ...params });
}

export async function symbolsAdmin(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/symbols/admin`, ...params });
}

export async function buildWebApp(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/build/app`, ...params });
}

export async function chatApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/chat`, ...params });
};

export async function getChatHistory(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/chat/history`, ...params });
};

export async function deleteChatConversation(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/chat/history`, ...params });
};

export async function getChatConfig(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/chat/config`, ...params });
};

export async function saveChatConfig(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/chat/config`, ...params });
};

export async function paymentApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/payment`, ...params });
};

// Admin-only. Backs the /admin/coupons page; operations: listCoupons,
// createCoupon, deactivateCoupon, and applyCouponToSubscription (attach a
// coupon to any customer's existing subscription — used by the admin
// CustomerDetail view). Customer-side coupon validation and self-service
// apply happen through paymentApi (operations: "validateCoupon",
// "previewApplyCoupon", "applyCoupon").
export async function couponApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/coupons`, ...params });
};

// Dev-only: the integration-test data endpoint backs both the integration
// suite's createCustomer flow and the new "Login as Random User" button on
// /login. Backend gates createRandomTestUser to non-prod stages.
export async function integTestData(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/integ/test/data`, ...params });
};

export async function transcribeEarnings(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT_V3}/earnings/transcribe`,
    ...params,
  });
}

export async function notifyUsersEventPublished(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT_V3}/earnings/notify`,
    ...params,
  });
}

export async function subscribeEmail(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT}/subscribe/email`,
    ...params,
  });
}

export async function getSubscription(params) {
  const { queryParams } = params || {};
  const combinedQueryParams = {
    query_type: `subscription`,
    ...queryParams,
  };
  return await doAxiosGet({ url: `${API_ENDPOINT}/web/subscription`, ...params, queryParams: combinedQueryParams });
};

export async function deleteSubscription(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/web/subscription`, ...params });
};

export async function getInvoices(params) {
  const { queryParams } = params || {};
  const combinedQueryParams = {
    query_type: `invoice`,
    ...queryParams,
  };
  return await doAxiosGet({ url: `${API_ENDPOINT}/web/subscription`, ...params, queryParams: combinedQueryParams });
};

export async function getInvoiceLink(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/web/invoice`, ...params });
};

export async function getApiKey(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/api/keys`, ...params });
}

export async function generateApiKey(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/api/keys`, ...params });
}

export async function deleteApiKey(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/api/keys`, ...params });
}

// Admin-only trial API key operations. These reuse the /api/keys endpoint and
// are dispatched by the `operation` field (admin_generate_trial_api_key,
// admin_list_trial_api_keys, admin_revoke_trial_api_key); the backend gates them
// with an admin-role check.
// Trial API keys are served by the dedicated admin endpoint (/a2/admin/api-keys),
// which dispatches on HTTP method (GET list / POST generate / DELETE revoke) — not
// on an `operation` field. It is admin-role gated and needs no subscription.
export async function adminListTrialApiKeys(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/admin/api-keys`, ...params });
}

export async function adminGenerateTrialApiKey(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/admin/api-keys`, ...params });
}

export async function adminRevokeTrialApiKey(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/admin/api-keys`, ...params });
}

export async function getProfile(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/profile`, ...params });
}

export async function saveProfile(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/profile`, ...params });
}

export async function deleteProfile(params) {
  return await doAxiosDelete({ url: `${API_ENDPOINT}/profile`, ...params });
}

export async function fetchCompany(exchange, symbol) {
  const url = `${API_ENDPOINT}/company?exchange=${exchange}&symbol=${symbol}`;
  return await doGet(url);
}

export async function getCompany(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/company`, ...params });
}

export async function updateCompany(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/company`, ...params });
}

export async function getSiteStats(appBuilderKey, params) {
  // console.log("getSiteStats:", params);
  let res = await doAxiosGet({
    url: `${API_GATEWAY_ENDPOINT}/site/stats`,
    context: {
      sessionContext: {
        appBuilderKey,
      },
    },
    ...params,
  });
  // console.log("Site stats:", res);
  if (!res) {
    console.error(
      "The backend API returned null siteStats!  This is unexpected."
    );
    return {
      earnings_events_recorded: 1,
      companies: 1,
      events_by_day: [],
      published_events_by_day: [],
    };
  }
  return res;
}

export async function getSP500Companies(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/companies/sp500`, ...params });
};

export const earningsQuery = async (queryParams, appBuilderKey) => {
  const results = await getEarnings({
    url: `${API_GATEWAY_ENDPOINT_V3}/earnings`,
    queryParams: {
      params: JSON.stringify(queryParams),
    },
    context: {
      sessionContext: {
        appBuilderKey,
      },
    },
  });
  // console.log("earningsResults:", results);
  return results.earnings.map(
    ({ quarter, year, exchange, symbol, primary_conference_date }) => {
      return {
        exchange,
        quarter: quarter.toUpperCase(),
        year,
        symbol,
        conference_date: primary_conference_date,
      };
    }
  );
};

const earningsWithCompanyName = async (queryParams, appBuilderKey) => {
  const result = await earningsQuery(queryParams, appBuilderKey);
  // console.log("earningsWithCompanyName queryParams: ", JSON.stringify(queryParams));
  // console.log("earningsWithCompanyName result:", result);
  const symbolsConfig = {
    endpoint: CLOUD_FRONT_ABSOLUTE_ENDPOINT,
  };
  // Ensure all values are non-null, truthy
  const filteredResult = result.filter(
    ({ quarter, year, exchange, symbol, conference_date }) =>
      !!quarter && !!year && !!exchange && !!symbol && !!conference_date
  );
  const allResults = await Promise.all(
    filteredResult.map(async ({ quarter, year, exchange, symbol, conference_date }) => {
      // console.log("Exchange:", exchange, "Symbol:", symbol);
      // TODO: First lookup _sym in symbols-v2, only if not found look up in all symbols
      let _sym = await getSymbol(exchange, symbol, symbolsConfig);
      // console.log("_sym", _sym);
      const { name = null } = _sym || {};
      if (!name) {
        log.warn(`Could not find company name for: ${exchange}_${symbol}`);
      }
      return {
        quarter,
        year,
        exchange,
        symbol,
        companyName: name,
        conference_date,
      };
    })
  );
  return allResults.filter(
    ({ quarter, year, exchange, symbol, companyName, conference_date }) =>
      quarter && year && exchange && symbol && companyName && conference_date
  ); // Ensure all values are non-null, truthy
};

export const getRecentCalls = async (appBuilderKey) => {
  const queryParams = {
    sort_by: "recording_stop_date",
    fields_present: [
      "audio_file",
      "exchange",
      "symbol",
      "primary_conference_date",
      "year",
      "quarter",
    ],
    fields_not_present: [],
    match: {
      is_published: true,
      primary: true,
    },
    size: 5,
  };
  log.debug("getRecentCalls()", queryParams);
  return await earningsWithCompanyName(queryParams, appBuilderKey);
};

export const earningsCallsQueryParams = () => {
  return {
    sort_by: "primary_conference_date",
    sort_order: "asc",
    fields_present: [
      "exchange",
      "symbol",
      "primary_conference_date",
      "year",
      "quarter",
    ],
    fields_not_present: [],
    match: {
      primary: true,
    },
    not_match: {
      news_wire: "test",
      no_conference_call: true,
    },
    filters: [hasConferenceFilter()],
    size: 1000,
  };
};

const hasConferenceFilter = () => {
  return {
    bool: {
      should: [
        {
          match_phrase: {
            has_teleconference: true,
          },
        },
        {
          match_phrase: {
            has_web_cast: true,
          },
        },
      ],
      minimum_should_match: 1,
    },
  };
};

export const getUpcomingCalls = async (appBuilderKey) => {
  log.debug("getUpcomingCalls()");
  const dateRangeFilter = {
    primary_conference_date: {
      format: "strict_date_optional_time",
      gte: new Date().toISOString(),
    },
  };
  const queryParams = {
    ...earningsCallsQueryParams(),
    range_filter: [dateRangeFilter],
  };
  return await earningsWithCompanyName(queryParams, appBuilderKey);
};

export const getEarningsCalendar = async ({
  startingDate = new Date(),
  daysBack = 30,
  daysForward = 30,
  appBuilderKey,
}) => {
  // console.log("earningsCalendar()");
  const dateRangeFilter = {
    primary_conference_date: {
      format: "strict_date_optional_time",
      gte: startOfDay(subDays(startingDate, daysBack)).toISOString(),
      lte: endOfDay(addDays(startingDate, daysForward)).toISOString(),
    },
  };
  const queryParams = {
    ...earningsCallsQueryParams(),
    range_filter: [dateRangeFilter],
    size: 2500,
  };
  return await earningsWithCompanyName(queryParams, appBuilderKey);
};

export async function identifyDevice(params) {
  return doAxiosGet({ url: `${API_ENDPOINT}/id`, ...params });
};

export async function newSession(params) {
  return doAxiosPost({ url: `${API_ENDPOINT}/login`, data: {}, ...params });
};

export async function destroySession(params) {
  return doAxiosPost({ url: `${API_ENDPOINT}/logout`, data: {}, ...params });
};

export async function updateAccountProfile({ firstName, lastName, company, context }) {
  return doAxiosPost({
    url: `${API_ENDPOINT}/account`,
    data: { first_name: firstName, last_name: lastName, company },
    context,
  });
};

export async function getTranscript(args, context) {
  // console.log(" -> getTranscript:", transcription_file);
  // console.log(`${API_GATEWAY_ENDPOINT_V3}/transcript`);
  return await doAxiosGet({
    url: `${API_GATEWAY_ENDPOINT_V3}/transcript`,
    queryParams: {
      ...args,
    },
    context,
  });
};

export async function recordAnalyticsEvents(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/tk`, ...params });
};

export async function pageLoadEvent(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/pg`, ...params });
};

export async function fileUpload(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/file/upload`, ...params });
};

export async function brockTestApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/brockTest`, ...params });
};

export async function getUsersAdmin(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT_V3}/users/admin`, ...params });
};

export async function adminUserRoles(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/users/admin`, ...params });
};

export async function getLiveEvents(params) {
  return await doAxiosGet({
    url: `${EARNINGS_CALL_DOT_DEV_ENDPOINT}/live-events`,
    ...params
  });
};

/**
 * Provisional live transcript for an in-progress earnings call, keyed by the opaque
 * HLS session `token` -- the same token embedded in the event's streamingUrl
 * (`.../live/{token}/playlist.m3u8`). Public + unauthenticated: same trust level as
 * the HLS playlist. Backend: EarningsCall/cca PR #508 `GET /a2/live-transcript/{token}`.
 *
 * We hit the execute-api origin directly (like `getTranscript`) instead of the
 * CloudFront web host: it avoids CDN caching on this fast-polling endpoint, and the
 * `/a2/live-transcript/*` CloudFront behavior is not wired on the web host anyway.
 *
 * Polling contract: `cursor` is the index of the NEXT segment wanted (start at 0). The
 * response is `{ status, next_cursor, segments: [{ segment_index, words: [{ w, start,
 * end }] }] }`; poll again with the returned `next_cursor`, and stop when `status` is
 * `"ended"`. A 404 means no session exists yet (the worker hasn't started writing) --
 * we surface it as `{ status: "pending", ... }` instead of throwing so callers keep polling.
 */
export async function getLiveTranscript({ token, cursor = 0 }) {
  const url = `${API_GATEWAY_ENDPOINT}/live-transcript/${encodeURIComponent(token)}?cursor=${cursor}`;
  const response = await fetch(url, { cache: "no-store", headers: { Accept: "application/json" } });
  if (response.status === 404) {
    return { status: "pending", next_cursor: cursor, segments: [] };
  }
  if (!response.ok) {
    const error = { status: response.status, statusText: response.statusText };
    try {
      error.responseBody = await response.text();
    } catch (e) {
      // ignore body read failures
    }
    log.warn("getLiveTranscript()", error);
    throw error;
  }
  return response.json();
};

export async function transcriptionSearch(params) {
  return await doAxiosGet({ url: `${EARNINGS_CALL_DOT_DEV_ENDPOINT}/search`, ...params });
};

// The full transcript for a logged-in visitor, from the API at runtime -- the same
// GET the mobile app makes (cca-mapp transcript_service.dart), authenticated by the
// `x-auth-type: session` / `x-auth-token` pair every call here sends. Level 3 is the
// one with per-word start times, which the player needs to follow the audio; the
// body is mapped into the page's shape by transcriptFromApi (src/transcriptPreview.mjs).
// Not the cca /a2/transcript the build uses: that endpoint is admin/app-builder/
// annotator only. Anonymous -> 401, which is the login gate being enforced by the
// server rather than by a CSS clamp (see TRANSCRIPT_PREVIEW_ONLY in api-config.mjs).
export async function getTranscriptFromApi({ exchange, symbol, year, quarter, ...params }) {
  return await doAxiosGet({
    url: `${EARNINGS_CALL_DOT_DEV_ENDPOINT}/transcript`,
    queryParams: {
      exchange: String(exchange).toLowerCase(),
      symbol: String(symbol).toLowerCase(),
      year: String(year),
      quarter: String(quarter).toLowerCase(),
      level: 3,
    },
    ...params,
  });
}

// The <audio> element reports only a MediaError code when a load fails, never
// the HTTP status behind it. After an auth-gated load has given up, this asks
// the media endpoint once, with the session headers the element could not
// carry, what it answers for the call: 429 is the WEB media bucket
// (api RateLimitConfig.webMediaConfig), 401 a session the cell cannot see yet,
// 5xx the host. Resolves to the status, or null when no response arrived. HEAD,
// so no audio bytes; validateStatus keeps a non-2xx from throwing (and from
// being retried by retry-axios): this is a diagnostic for the AudioLoadError
// analytics event, not a load.
export async function getAudioStatusFromApi({ exchange, symbol, year, quarter, context }) {
  const headers = await getRequestHeaders({ context });
  try {
    const response = await axios({
      method: "head",
      url: `${EARNINGS_CALL_DOT_DEV_ENDPOINT}/audio`,
      params: {
        exchange: String(exchange).toLowerCase(),
        symbol: String(symbol).toLowerCase(),
        year: String(year),
        quarter: String(quarter).toLowerCase(),
      },
      headers,
      validateStatus: () => true,
    });
    return response.status;
  } catch (error) {
    log.error("Audio status probe failed:", error);
    return null;
  }
}

export async function deEntitySearch(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/de-entity/search`, ...params });
};

export async function getAdminQuotes(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT_V3}/admin/quotes`, ...params });
};

export async function postAdminQuotes(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/quotes`, ...params });
};

export async function getCustomerQuote(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quotes`, ...params });
};

export async function getNotificationsV2(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

export async function markNotificationReadV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

export async function getContractStatus(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/quotes/accept-contract`, ...params });
};

export async function recordContractAcceptance(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/quotes/accept-contract`, ...params });
};

export async function markAllNotificationsReadV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

export async function createNotificationV2(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/notifications/v2`, ...params });
};

/**
 * Send a browser control command via HTTP.
 * @param {Object} params
 * @param {Object} params.data - { event_id, action, x?, y?, text?, keys?, scroll_x?, scroll_y? }
 */
export async function sendBrowserControlHttp(params) {
  return await doAxiosPost({
    url: `${API_ENDPOINT}/browser-control`,
    ...params,
  });
}

export async function adminBillingApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/billing`, ...params });
};

export async function saveSecretAdmin(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/secrets`, ...params });
};

export async function submitReviewG2Intent(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/review/g2-intent`, ...params });
};

export async function submitReviewFeedback(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/review/feedback`, ...params });
};

export async function adminReviewApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT_V3}/admin/reviews`, ...params });
};

// Admin daily operational stats (per-day recording / publish-latency / slide-deck stats)
export async function adminDailyStatsApi(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/admin/daily-stats`, ...params });
}
// Admin support-chat transcripts. List with { size, results_from }, or detail
// with { conversation_id } — same endpoint, the backend switches on the param.
export async function adminSupportChatsApi(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/admin/support-chats`, ...params });
}
// Fleet-wide cache-reconciler status: one row per Atlas box, each carrying either
// that box's `GET /ops/cache/reconcile` body or the error that came back instead, so
// one unreachable region degrades to one red card rather than failing the page.
//
// `/ops/*`, NOT `/internal/*`: the split is the credential boundary. `/ops/*` takes
// an admin session, `/internal/*` stays token-only because a heap dump carries live
// API keys.
//
// Authenticated by the `x-auth-type: session` / `x-auth-token` pair every other call
// here sends. OpsAuthFilter accepts the session cookie too, but only from an
// earningscall.biz origin -- and the integration suite serves the app from
// localhost, so a cookie-only board is one no test can drive. The header costs a
// CORS preflight, cached an hour by the boxes' `access-control-max-age`.
export async function fleetReconcilerStatusApi(params) {
  return await doAxiosGet({
    url: `${ATLAS_FLEET_ENDPOINT}/ops/fleet/reconcile`,
    ...params,
  });
}

// Start a reconciler lap on ONE box. Not retried on failure, by rax's default
// httpMethodsToRetry (POST is absent from it) and deliberately so: a lap is a
// long-running origin-read sweep, and a retry that raced a slow-but-successful
// first attempt would be answered with the handler's own 409 anyway.
// Keyed by box NAME (`atlas-prod-eu-1`), not region: region is unique only within a
// stage -- alpha and prod both have one called "eu" -- so a region-keyed POST could
// start a sweep on the wrong box.
export async function runReconcilerLapApi({ box, ...params }) {
  return await doAxiosPost({
    url: `${ATLAS_FLEET_ENDPOINT}/ops/fleet/reconcile`,
    queryParams: { box },
    ...params,
  });
}

// Save transcript edits API
export async function editTranscriptApi(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/transcript/edit`, ...params });
}

// Internal accounting: the chart of accounts.
//
// One URL, two verbs -- GET lists, POST dispatches on an `action` field
// ("seed-defaults", plus create/update/deactivate the M0 UI does not call yet).
// The backend gates every mutation on the admin role and answers 403 otherwise,
// so a BILLING user can read the chart but not seed it.
export async function accountingAccountsGet(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/accounting/accounts`, ...params });
}

export async function accountingAccountsPost(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/accounting/accounts`, ...params });
}

// Internal accounting: the money sources (Stripe, Mercury, Plaid) and what they
// expose. POST-only, dispatching on `action`: "list" reads every connection AND
// the external accounts behind them in one response, "sync-now" runs a bounded
// backfill inline and answers with the sync job's counts. Reads allow admin or
// billing; every mutation is admin-only server side and answers 403 otherwise.
export async function accountingConnectionsPost(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/accounting/connections`, ...params });
}

// Internal accounting: connecting a bank through Plaid. Two calls with a human
// in the middle -- "create-link-token" mints the token Plaid Link opens with,
// then "exchange-public-token" turns what Link hands back into a connection.
// Both actions write, so both are admin-only. `environment` is required on both
// ("test" | "production") and deliberately has no default: the wrong value here
// would connect the real bank.
export async function accountingPlaidLinkPost(params) {
  return await doAxiosPost({ url: `${API_ENDPOINT}/accounting/plaid/link`, ...params });
}

// Internal accounting: the normalized transaction feed. GET with filter query
// params (external_account_id, from, to, categorization_status, limit, cursor)
// answering { events: [...], next_cursor: null | string }. Cursor-paged rather
// than offset-paged because it reads a DynamoDB index, so there is no page
// number to jump to -- only "the next page after this one".
export async function accountingEventsGet(params) {
  return await doAxiosGet({ url: `${API_ENDPOINT}/accounting/events`, ...params });
}
